/* ============================================================
   Featured Products — behavior

   - Heading words animate in once, the first time the section
     scrolls into view (IntersectionObserver + CSS animation,
     GSAP timeline used instead if available for smoother easing
     — GSAP is optional too: if it doesn't load, the plain CSS
     animation below just runs instead, so nothing here is a hard
     dependency any more)
   - If every card already fits in the row without needing to
     scroll, the row is centered instead of sitting flush left
   - Tapping a card on a touch device reveals its floating
     elements + add-to-cart panel (the same reveal that happens
     on `:hover` with a mouse) — a second tap, or tapping another
     card, dismisses it
   - Variant select updates the displayed price and which
     variant add-to-cart will use
   - Add to cart: POSTs to /cart/add.js, shows a spinner while
     in flight, then dispatches `cart:updated` so the header's
     cart count (see header.js) updates automatically
   ============================================================ */

function initAllFeaturedProducts(root) {
  var scope = root || document;
  scope.querySelectorAll('[data-featured-products]').forEach(function (section) {
    if (section.dataset.fpInit === 'true') return;
    section.dataset.fpInit = 'true';
    initFeaturedProducts(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initAllFeaturedProducts();
  });
} else {
  initAllFeaturedProducts();
}

document.addEventListener('shopify:section:load', function (event) {
  initAllFeaturedProducts(event.target);
});

function initFeaturedProducts(section) {
  /* ---------- Heading word reveal on scroll into view ---------- */
  var heading = section.querySelector('[data-heading-words="true"]');
  if (heading && 'IntersectionObserver' in window) {
    var words = heading.querySelectorAll('[data-word]');

    var headingObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          if (typeof window.gsap !== 'undefined') {
            window.gsap.fromTo(
              words,
              { y: '120%', opacity: 0 },
              { y: '0%', opacity: 1, duration: 0.7, stagger: 0.08, ease: 'power3.out' }
            );
          } else {
            heading.classList.add('is-revealed');
            words.forEach(function (word, i) {
              word.style.animationDelay = i * 0.08 + 's';
            });
          }

          headingObserver.unobserve(heading);
        });
      },
      { threshold: 0.3 }
    );

    headingObserver.observe(heading);
  }

  var cards = section.querySelectorAll('[data-product-card]');

  /* ---------- Center the row if it doesn't need to scroll ---------- */
  updateTrackFit(section);
  window.addEventListener('resize', function () {
    updateTrackFit(section);
  });

  /* ---------- Tap a card on touch: reveals it, second tap dismisses ---------- */
  var isTouch = window.matchMedia('(hover: none)').matches;
  if (isTouch) {
    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('[data-add-to-cart]') || e.target.closest('[data-variant-select]')) return;
        var wasActive = card.classList.contains('is-active');
        cards.forEach(function (c) {
          c.classList.remove('is-active');
        });
        if (!wasActive) card.classList.add('is-active');
      });
    });
  }

  /* ---------- Whole-card click -> product page ----------
     Excludes the variant select and the Add to cart button, which
     keep their own behavior. Safe alongside swipe-scrolling: the
     browser doesn't fire a synthetic `click` after a touch
     gesture that involved real touchmove movement (an actual
     drag/swipe) — only a genuine tap triggers this. */
  cards.forEach(function (card) {
    var productUrl = card.dataset.productUrl;
    if (!productUrl) return;

    card.addEventListener('click', function (e) {
      if (e.target.closest('[data-add-to-cart]') || e.target.closest('[data-variant-select]')) return;
      window.location.href = productUrl;
    });
  });

  /* ---------- Variant select -> price + add-to-cart target ---------- */
  cards.forEach(function (card) {
    var select = card.querySelector('[data-variant-select]');
    var addButton = card.querySelector('[data-add-to-cart]');
    var priceDisplay = card.querySelector('[data-price-display]');

    if (select && addButton) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        addButton.setAttribute('data-variant-id', option.value);
        addButton.disabled = option.disabled;
        if (priceDisplay && option.dataset.price) {
          priceDisplay.textContent = option.dataset.price;
        }
      });
    }

    /* ---------- Add to cart ---------- */
    if (addButton) {
      addButton.addEventListener('click', function () {
        if (addButton.disabled || addButton.classList.contains('is-loading')) return;

        var variantId = addButton.getAttribute('data-variant-id');
        if (!variantId) return;

        addButton.classList.add('is-loading');
        addButton.classList.remove('is-added');

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed');
            return response.json();
          })
          .then(function () {
            return fetch('/cart.js').then(function (r) {
              return r.json();
            });
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count } })
            );
            if (typeof window.updateHeaderCartCount === 'function') {
              window.updateHeaderCartCount(cart.item_count);
            }
            if (window.OBVSLYCartDrawer && typeof window.OBVSLYCartDrawer.flyToCart === 'function') {
              window.OBVSLYCartDrawer.flyToCart(addButton);
            }

            addButton.classList.remove('is-loading');
            addButton.classList.add('is-added');
            window.setTimeout(function () {
              addButton.classList.remove('is-added');
            }, 1800);
          })
          .catch(function () {
            addButton.classList.remove('is-loading');
            addButton.classList.add('is-error');
            window.setTimeout(function () {
              addButton.classList.remove('is-error');
            }, 1800);
          });
      });
    }
  });
}

/* Adds/removes .fp-fits on the track depending on whether its
   content is wider than the visible area — when everything
   already fits, the row is centered (via CSS) instead of sitting
   flush against the left edge like a scrollable row normally
   would. Re-checked on resize since rotating a phone or resizing
   a desktop window can change which state applies. */
function updateTrackFit(section) {
  var track = section.querySelector('[data-track]');
  if (!track) return;
  var fits = track.scrollWidth <= track.clientWidth + 1;
  track.classList.toggle('fp-fits', fits);
}